import React, { useState, useEffect, useCallback } from 'react';
import { apiJson, apiPut } from '../api';
import { colors, theme } from '../styles';

const TIMEZONES = [
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii',
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland', 'America/Toronto', 'America/Sao_Paulo', 'America/Mexico_City',
];

const ACTION_LABELS = {
  like: { name: 'Likes', color: colors.red },
  comment: { name: 'Comments', color: colors.blue },
  follow: { name: 'Follows', color: colors.green },
  unfollow: { name: 'Unfollows', color: colors.orange },
};

export default function Settings({ account, onAccountUpdated }) {
  const [settings, setSettings] = useState(null);
  const [actionSettings, setActionSettings] = useState({});
  const [saved, setSaved] = useState(false);
  const [accForm, setAccForm] = useState({ display_name: '', login_user: '', login_pass: '', proxy_url: '', manual_login: 0 });
  const [accSaved, setAccSaved] = useState(false);

  const load = useCallback(async () => {
    const [sData, asData] = await Promise.all([
      apiJson(`/accounts/${account.id}/settings`),
      apiJson(`/accounts/${account.id}/action-settings`),
    ]);
    if (sData) setSettings(sData);
    if (asData) setActionSettings(asData.action_settings || {});
  }, [account.id]);

  useEffect(() => {
    load();
    setAccForm({
      display_name: account.display_name || '',
      login_user: account.login_user || '',
      login_pass: '',
      proxy_url: account.proxy_url || '',
      manual_login: account.manual_login || 0,
    });
  }, [load, account]);

  const saveSettings = async () => {
    const { account_id, actions_today, actions_today_date, ...rest } = settings;
    await apiPut(`/accounts/${account.id}/settings`, rest);
    // Save action type settings
    for (const [at, s] of Object.entries(actionSettings)) {
      const { id, account_id: _a, action_type, ...fields } = s;
      await apiPut(`/accounts/${account.id}/action-settings/${at}`, fields);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveAccount = async () => {
    const payload = {};
    if (accForm.display_name !== (account.display_name || '')) payload.display_name = accForm.display_name;
    if (accForm.login_user !== (account.login_user || '')) payload.login_user = accForm.login_user;
    if (accForm.login_pass) payload.login_pass = accForm.login_pass;
    if (accForm.proxy_url !== (account.proxy_url || '')) payload.proxy_url = accForm.proxy_url;
    if (accForm.manual_login !== (account.manual_login || 0)) payload.manual_login = accForm.manual_login;
    if (Object.keys(payload).length === 0) { setAccSaved(true); setTimeout(() => setAccSaved(false), 2000); return; }
    await apiPut(`/accounts/${account.id}`, payload);
    setAccSaved(true);
    setAccForm(f => ({ ...f, login_pass: '' }));
    setTimeout(() => setAccSaved(false), 2000);
    if (onAccountUpdated) onAccountUpdated();
  };

  if (!settings) return <div style={{ color: colors.textDim }}>Loading...</div>;

  const updateAS = (at, key, val) => {
    setActionSettings(prev => ({ ...prev, [at]: { ...prev[at], [key]: val } }));
  };

  const toggle = (label, key, desc = '') => (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
      background: colors.bgInput, borderRadius: 6, cursor: 'pointer',
      border: `1px solid ${settings[key] ? colors.accent : colors.borderLight}`,
    }}>
      <input type="checkbox" checked={!!settings[key]}
        onChange={e => setSettings({ ...settings, [key]: e.target.checked ? 1 : 0 })}
        style={{ accentColor: colors.accent }} />
      <div>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ color: colors.textDim, fontSize: 11 }}>{desc}</div>}
      </div>
    </label>
  );

  return (
    <div>
      <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 16px' }}>Settings: @{account.username}</h2>

      {/* Account & Credentials */}
      <div style={{ ...theme.card, marginBottom: 12, borderColor: colors.accent + '44' }}>
        <h3 style={theme.sectionTitle}>Account & Credentials</h3>
        <div style={theme.row}>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Display Name</label>
            <input style={theme.input} value={accForm.display_name} onChange={e => setAccForm({ ...accForm, display_name: e.target.value })} placeholder="My Account" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Proxy URL</label>
            <input style={theme.input} value={accForm.proxy_url} onChange={e => setAccForm({ ...accForm, proxy_url: e.target.value })} placeholder="socks5://user:pass@host:port" />
          </div>
        </div>
        <div style={theme.row}>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Platform Login</label>
            <input style={theme.input} value={accForm.login_user} onChange={e => setAccForm({ ...accForm, login_user: e.target.value })} placeholder="email or username" autoComplete="off" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Platform Password {account.has_credentials && <span style={{ color: colors.green, fontSize: 10 }}>(stored)</span>}</label>
            <input type="password" style={theme.input} value={accForm.login_pass} onChange={e => setAccForm({ ...accForm, login_pass: e.target.value })} placeholder={account.has_credentials ? 'leave blank to keep' : 'enter password'} autoComplete="off" />
          </div>
        </div>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginTop: 10,
          background: accForm.manual_login ? colors.yellow + '11' : colors.bgInput,
          borderRadius: 6, cursor: 'pointer',
          border: `1px solid ${accForm.manual_login ? colors.yellow : colors.borderLight}`,
        }}>
          <input type="checkbox" checked={!!accForm.manual_login}
            onChange={e => setAccForm({ ...accForm, manual_login: e.target.checked ? 1 : 0 })}
            style={{ accentColor: colors.yellow }} />
          <div>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>Manual Login Required</div>
            <div style={{ color: colors.textDim, fontSize: 11 }}>Enable if this account uses social login (Google, Facebook, etc.) and cannot auto-login with credentials. The system will prompt for manual login via Live View.</div>
          </div>
        </label>
        <div style={{ marginTop: 12 }}>
          <button style={theme.btnPrimary} onClick={saveAccount}>{accSaved ? 'Saved!' : 'Save Account Details'}</button>
        </div>
      </div>

      {/* Cookie Import */}
      <CookieImporter account={account} />

      {/* Continuous Mode */}
      <div style={{ ...theme.card, marginBottom: 12, borderColor: settings.continuous_mode ? colors.green + '66' : colors.border }}>
        <h3 style={theme.sectionTitle}>Run Mode</h3>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          background: settings.continuous_mode ? colors.green + '11' : colors.bgInput,
          borderRadius: 6, cursor: 'pointer',
          border: `1px solid ${settings.continuous_mode ? colors.green : colors.borderLight}`,
        }}>
          <input type="checkbox" checked={!!settings.continuous_mode}
            onChange={e => setSettings({ ...settings, continuous_mode: e.target.checked ? 1 : 0 })}
            style={{ accentColor: colors.green, width: 18, height: 18 }} />
          <div>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Continuous Mode</div>
            <div style={{ color: colors.textDim, fontSize: 11 }}>Run sessions automatically until stopped. Sleeps between sessions.</div>
          </div>
        </label>
        {!!settings.continuous_mode && (
          <div style={{ ...theme.row, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Min Sleep Between Sessions (min)</label>
              <input type="number" style={theme.input} value={settings.sleep_min_minutes ?? 30}
                onChange={e => setSettings({ ...settings, sleep_min_minutes: parseFloat(e.target.value) || 0 })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Max Sleep Between Sessions (min)</label>
              <input type="number" style={theme.input} value={settings.sleep_max_minutes ?? 90}
                onChange={e => setSettings({ ...settings, sleep_max_minutes: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>
        )}
      </div>

      {/* Hours & Limits */}
      <div style={{ ...theme.card, marginBottom: 12 }}>
        <h3 style={theme.sectionTitle}>Hours & Daily Limits</h3>
        <div style={theme.row}>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Timezone</label>
            <select style={theme.select} value={settings.timezone || 'US/Central'}
              onChange={e => setSettings({ ...settings, timezone: e.target.value })}>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Start Hour (0-23)</label>
            <input type="number" style={theme.input} value={settings.hours_start ?? 8} min={0} max={23}
              onChange={e => setSettings({ ...settings, hours_start: parseInt(e.target.value) || 0 })} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>End Hour (0-23)</label>
            <input type="number" style={theme.input} value={settings.hours_end ?? 22} min={0} max={23}
              onChange={e => setSettings({ ...settings, hours_end: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <div style={theme.row}>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Max Actions Per Day (all types combined)</label>
            <input type="number" style={theme.input} value={settings.max_actions_per_day ?? 100}
              onChange={e => setSettings({ ...settings, max_actions_per_day: parseInt(e.target.value) || 0 })} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Variation Factor (0-1)</label>
            <input type="number" style={theme.input} value={settings.variation_factor ?? 0.3} step={0.05} min={0} max={1}
              onChange={e => setSettings({ ...settings, variation_factor: parseFloat(e.target.value) || 0 })} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Max Following Cap (0 = no limit)</label>
            <input type="number" style={theme.input} value={settings.max_following_cap ?? 0} min={0}
              onChange={e => setSettings({ ...settings, max_following_cap: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <div style={{ marginTop: 4, fontSize: 11, color: colors.textDimmer }}>
          Today: {settings.actions_today || 0} / {settings.max_actions_per_day} actions used
        </div>
      </div>

      {/* Enabled Actions */}
      <div style={{ ...theme.card, marginBottom: 12 }}>
        <h3 style={theme.sectionTitle}>Enabled Actions</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {toggle('Likes', 'likes_enabled', 'Auto-like posts from targets')}
          {toggle('Comments', 'comments_enabled', 'Auto-comment using templates')}
          {toggle('Follows', 'follows_enabled', 'Auto-follow target accounts')}
          {toggle('Unfollows', 'unfollows_enabled', 'Auto-unfollow (respects whitelist)')}
        </div>
      </div>

      {/* Per-Action Settings */}
      <div style={{ ...theme.card, marginBottom: 12 }}>
        <h3 style={theme.sectionTitle}>Per-Action Pacing & Limits</h3>
        <div style={{ fontSize: 11, color: colors.textDimmer, marginBottom: 12 }}>
          Each action type has its own delay range and min/max count per session. The system randomly picks a target count between min and max each session.
        </div>
        {Object.entries(ACTION_LABELS).map(([at, meta]) => {
          const s = actionSettings[at] || {};
          const enabled = settings[`${at}s_enabled`];
          return (
            <div key={at} style={{
              marginBottom: 10, padding: 12, borderRadius: 6,
              background: enabled ? colors.bgInput : colors.bgDark,
              border: `1px solid ${enabled ? meta.color + '44' : colors.border}`,
              opacity: enabled ? 1 : 0.4,
            }}>
              <div style={{ color: meta.color, fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{meta.name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <label style={theme.label}>Min Delay (s)</label>
                  <input type="number" style={theme.input} value={s.min_delay ?? ''} disabled={!enabled}
                    onChange={e => updateAS(at, 'min_delay', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={theme.label}>Max Delay (s)</label>
                  <input type="number" style={theme.input} value={s.max_delay ?? ''} disabled={!enabled}
                    onChange={e => updateAS(at, 'max_delay', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={theme.label}>Min / Session</label>
                  <input type="number" style={theme.input} value={s.min_per_session ?? ''} disabled={!enabled}
                    onChange={e => updateAS(at, 'min_per_session', parseInt(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={theme.label}>Max / Session</label>
                  <input type="number" style={theme.input} value={s.max_per_session ?? ''} disabled={!enabled}
                    onChange={e => updateAS(at, 'max_per_session', parseInt(e.target.value) || 0)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button style={theme.btnPrimary} onClick={saveSettings}>{saved ? 'Saved!' : 'Save All Settings'}</button>
    </div>
  );
}

function CookieImporter({ account }) {
  const [showImport, setShowImport] = useState(false);
  const [cookieJson, setCookieJson] = useState('');
  const [importStatus, setImportStatus] = useState(null);

  const doImport = async () => {
    if (!cookieJson.trim()) return;
    setImportStatus('Importing...');
    try {
      const res = await apiPost(`/accounts/${account.id}/import-cookies`, { cookies_json: cookieJson });
      if (res && res.ok) {
        setImportStatus(`Imported ${res.count} cookies`);
        setCookieJson('');
        setTimeout(() => { setImportStatus(null); setShowImport(false); }, 3000);
      } else {
        setImportStatus('Failed — check JSON format');
      }
    } catch (e) {
      setImportStatus('Error importing');
    }
  };

  return (
    <div style={{ ...theme.card, marginBottom: 12, borderColor: colors.blue + '44' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ ...theme.sectionTitle, margin: 0 }}>Cookie Import</h3>
        <button style={{ ...theme.btnSmall, background: colors.blue, color: '#fff', fontWeight: 600 }}
          onClick={() => setShowImport(!showImport)}>
          {showImport ? 'Close' : 'Import Cookies'}
        </button>
      </div>
      {!showImport && (
        <div style={{ fontSize: 11, color: colors.textDim, marginTop: 6 }}>
          Import browser cookies to bypass login. Useful when auto-login is blocked.
        </div>
      )}
      {showImport && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.8, marginBottom: 10 }}>
            <strong style={{ color: colors.text }}>Steps:</strong><br />
            1. Install a cookie editor extension (e.g. "EditThisCookie" or "Cookie-Editor") in your browser<br />
            2. Go to <strong>tiktok.com</strong> (where you're already logged in)<br />
            3. Click the extension icon and choose <strong>"Export All"</strong> or copy all cookies as JSON<br />
            4. Paste the entire JSON below and click Import<br />
            <span style={{ color: colors.textDim }}>Export from either www.tiktok.com or tiktok.com — both work. The system uses all cookies regardless of subdomain.</span>
          </div>
          <textarea
            style={{
              ...theme.input, minHeight: 120, fontFamily: 'monospace', fontSize: 11,
              resize: 'vertical',
            }}
            value={cookieJson}
            onChange={e => setCookieJson(e.target.value)}
            placeholder='Paste exported cookies JSON here...'
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button style={theme.btnSuccess} onClick={doImport}>Import Cookies</button>
            {importStatus && (
              <span style={{
                fontSize: 12,
                color: importStatus.startsWith('Imported') ? colors.green : colors.orange,
              }}>{importStatus}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
